import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { MENU } from "./menu-config";

type Tab = { key: string; label: string; path: string };
type State = {
  tabs: Tab[];
  open: (t: Tab) => void;
  close: (key: string) => void;
  closeAll: () => void;
};

export const useTabs = create<State>()(
  persist(
    (set, get) => ({
      tabs: [],
      open: (t) => {
        const cur = get().tabs;
        if (!cur.some((x) => x.key === t.key)) set({ tabs: [...cur, t] });
      },
      close: (key) => set({ tabs: get().tabs.filter((t) => t.key !== key) }),
      // #4342: đóng nhanh TẤT CẢ tab đang mở.
      closeAll: () => set({ tabs: [] }),
    }),
    { name: "pkhoffice-tabs" },
  ),
);

function labelForPath(pathname: string): string | null {
  // Dashboard
  if (pathname === "/" || pathname === "/dashboard") return "Tổng quan";
  // #4132: phải chọn mục KHỚP CHÍNH XÁC trước, nếu không thì mục có đường dẫn
  // DÀI NHẤT (cụ thể nhất). Trước đây lấy match ĐẦU TIÊN theo startsWith, mà
  // "Văn bản cá nhân" (/documents) là tiền tố của mọi trang con
  // (/documents/incoming, /documents/published…) nên mọi tab con đều bị gán nhãn
  // "Văn bản cá nhân".
  const all: { to: string; label: string }[] = [];
  for (const g of MENU) {
    if (g.to) all.push({ to: g.to, label: g.label });
    for (const sub of g.items ?? []) all.push({ to: sub.to, label: sub.label });
  }
  const exact = all.find((x) => x.to === pathname);
  if (exact) return exact.label;
  const prefix = all
    .filter((x) => pathname.startsWith(x.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefix ? prefix.label : null;
}

export function TabBar() {
  const loc = useLocation();
  const nav = useNavigate();
  const tabs = useTabs((s) => s.tabs);
  const open = useTabs((s) => s.open);
  const close = useTabs((s) => s.close);
  const closeAll = useTabs((s) => s.closeAll);

  useEffect(() => {
    const label = labelForPath(loc.pathname);
    if (!label) return;
    const key = loc.pathname.split("/").slice(0, 3).join("/") || "/";
    open({ key, label, path: loc.pathname });
  }, [loc.pathname, open]);

  const activeKey = (() => {
    const key = loc.pathname.split("/").slice(0, 3).join("/") || "/";
    return key;
  })();

  if (tabs.length === 0) return null;

  return (
    <div className="relative flex items-end gap-0.5 px-3 pt-2 bg-transparent overflow-x-auto">
      {tabs.map((t) => {
        const active = t.key === activeKey;
        return (
          <div
            key={t.key}
            className={cn(
              "group relative flex items-center gap-2 h-9 pl-3 pr-2 rounded-t-md cursor-pointer select-none text-sm whitespace-nowrap transition-colors",
              active
                ? "bg-background text-foreground shadow-soft border border-border border-b-0"
                : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
            onClick={() => nav(t.path)}
          >
            <span className="truncate max-w-[180px]">{t.label}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const remaining = tabs.filter((x) => x.key !== t.key);
                close(t.key);
                if (active && remaining.length) nav(remaining[remaining.length - 1].path);
                else if (active && !remaining.length) nav("/dashboard");
              }}
              className="h-5 w-5 grid place-items-center rounded opacity-60 hover:opacity-100 hover:bg-black/5"
              aria-label="Close tab"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
      {/* #4342: nút đóng nhanh tất cả tab → về Tổng quan */}
      <button
        onClick={() => {
          closeAll();
          nav("/dashboard");
        }}
        className="ml-1 h-9 flex items-center gap-1.5 px-3 rounded-t-md text-sm whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        title="Đóng tất cả tab đang mở"
      >
        <X className="h-3.5 w-3.5" />
        <span>Đóng tất cả</span>
      </button>
      {/* bottom continuation line under all but active */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-border -z-10" />
    </div>
  );
}
